<template>
	<div class="experience-calendar">
		<ModalHeader :title="getString('exceptions')" :button-text="getString('save')" @click-button="confirmException" />
		<div class="calendar-container">
			<DatePicker ref="date-picker" v-model="date" mode="date" color="teal" :min-date='new Date()' :is-dark="userStore.darkMode" :attributes='attributes'	/>
		</div>
		<div class="weekday-container" :class="{ 'with-exception': currentException }">
			<ExperienceTimeslotWeekday :weekday="dateWeekday" no-copy :timeslots="timeslotsShown" @delete="removeTimeslot" @add="addTimeslot" @edit="editTimeslot" />
			<div class="actions" v-if="currentException">
				<div class="action" v-html="svgStore.get('iconBin')" @click="removeException"></div>
			</div>
		</div>
	</div>
</template>

<script>
import {DatePicker} from "v-calendar";
import {useExperiencesStore, useUserStore} from "@/console-new/stores";
import ExperienceTimeslotWeekday from "@/console-new/components/plugins/reservations/ExperienceTimeslotWeekday.vue";
import API from "@/shared/helpers/API";
import {formatDateOnly, getLocalDateFromServer} from "@/shared/helpers/dates";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";

export default {
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const experiencesStore = useExperiencesStore();
		const notificationStore = useNotificationStore();
		const svgStore = useSvgStore();
		return {userStore, getString: languageStore.getString, experiencesStore, showNotification: notificationStore.showNotification, svgStore};
	},
	name: "ExperienceCalendar",
	components: {ModalHeader, ExperienceTimeslotWeekday, DatePicker},
	emits: ['close'],
    props: {
		experienceId: {
			type: String,
			required: true
		}
    },
	data() {
		return {
			date: new Date(),
			timeslotsShown: [],
		};
	},
	computed: {
		experience() {
			return this.experiencesStore.businessExperiences.find(experience => experience.experience_id === this.experienceId);
		},
		dateWeekday() {
			return this.date ? (this.date.getDay() + 6) % 7 : 0;
		},
		attributes() {
			let result = [];

			let exampleDate = new Date();
			exampleDate.setDate(exampleDate.getDate() + 2);
			let exampleDate2 = new Date();
			exampleDate2.setDate(exampleDate2.getDate() + 3);
			let exampleDate3 = new Date();
			exampleDate3.setDate(exampleDate3.getDate() + 4);

			//Add dates with exceptions
			this.experience.exceptions.forEach(e => {
				result.push({
					key: e.experience_timeslot_exception_id+"",
					content: 'Exception',
					highlight: {
						color: 'pink',
						fillMode: 'outline',
					},
					dates: [e.date],
				});
			});

			return result;
		},
		currentException() {
			return this.experience.exceptions.find(e => formatDateOnly(e.date) === formatDateOnly(this.date));
		}
	},
	methods: {
		removeException() {
			if(this.currentException) {
				this.experience.exceptions = this.experience.exceptions.filter(e => e.experience_timeslot_exception_id !== this.currentException.experience_timeslot_exception_id);
				this.updateTimeslots();
			}
		},
		confirmException() {

			let exceptions = this.experience.exceptions.map(e => {
				return {
					...e,
					date: formatDateOnly(e.date),
				}
			});

			API.init().saveExperienceExceptions(this.userStore.currBusinessId, this.experienceId, JSON.stringify(exceptions), r => {
				if(r && r.result && r.result === "OK") {
					this.showNotification("generic-success");
					this.$emit('close');
				} else {
					this.showNotification("generic-fail");
				}
			});

		},
		updateTimeslots() {

			//if date is exception
			if(this.currentException) {
				this.timeslotsShown = this.currentException.timeslots;
			} else {
				this.timeslotsShown = this.experience.timeslots.filter(t => t.weekday === this.dateWeekday).map(t => {
					return {
						...t,
						experience_timeslot_id: "clone" + Math.random(),
					}
				});
			}

		},
		removeTimeslot(id) {
			this.timeslotsShown = this.timeslotsShown.filter(t => t.experience_timeslot_id !== id);

			if(this.currentException) {
				this.currentException.timeslots = this.currentException.timeslots.filter(t => t.experience_timeslot_id !== id);
			} else {
				let newException = {
					experience_timeslot_exception_id: "new" + Math.random(),
					date: this.date,
					timeslots: this.timeslotsShown,
				}
				this.experience.exceptions.push(newException);
			}
		},
		addTimeslot(newTimeslot) {
			this.timeslotsShown.push(newTimeslot);

			if(this.currentException) {
				this.currentException.timeslots = this.timeslotsShown;
			} else {
				let newException = {
					experience_timeslot_exception_id: "new" + Math.random(),
					date: this.date,
					timeslots: this.timeslotsShown,
				}
				this.experience.exceptions.push(newException);
			}

		},
		editTimeslot(timeslot) {

			if(!this.currentException) {
				let newException = {
					experience_timeslot_exception_id: "new" + Math.random(),
					date: this.date,
					timeslots: this.timeslotsShown,
				}
				this.experience.exceptions.push(newException);
			}
		}
	},
    mounted() {

        API.init().getExperienceExceptions(this.userStore.currBusinessId, this.experienceId, r => {
			let exceptionsTimeslots = {};
			r.exceptions_timeslots.forEach(t => {
				if(!t.experience_timeslot_exception_id) {
					return;
				}
				if(!exceptionsTimeslots.hasOwnProperty(t.experience_timeslot_exception_id)) {
					exceptionsTimeslots[t.experience_timeslot_exception_id] = [];
				}
				exceptionsTimeslots[t.experience_timeslot_exception_id].push(this.experiencesStore.normalizeTimeslot(t));
			});
            this.experience.exceptions = r.exceptions.map(t => {

                return {
					experience_timeslot_exception_id: Number(t.experience_timeslot_exception_id),
					date: getLocalDateFromServer(t.date),
					timeslots: exceptionsTimeslots.hasOwnProperty(t.experience_timeslot_exception_id) ? exceptionsTimeslots[t.experience_timeslot_exception_id] : [],
                }

            });

			this.updateTimeslots();
        });

    },
	watch: {
		date() {
			this.updateTimeslots();
		}
	}
}
</script>

<style scoped lang="scss">
.experience-calendar {
	display: flex;
	flex-direction: column;

	.calendar-container {
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		margin-bottom: 8px;
	}

	.weekday-container {
		background-color: var(--color-bg);
		border-radius: 13px;
		margin-bottom: 8px;
		box-shadow: 0 2px 2px var(--color-box-shadow-2);
		display: flex;
		gap: 8px;
		padding-right: 8px;

		.experience-timeslot-weekday {
			border-bottom: none;
		}

		&.with-exception:deep(.week-row) {
			padding-right: 0;
		}


		.actions {
			flex-shrink: 0;
			display: flex;
			gap: 8px;
			align-items: center;

			.action {
				border-radius: 13px;
				border: 1px solid $primary;
				display: flex;
				justify-content: center;
				align-items: center;
				padding: 9px;
				cursor: pointer;
				flex-shrink: 0;

				:deep(svg) {
					width: 16px;
					height: 16px;

					.fill {
						fill: $primary;
					}

					.stroke {
						stroke: $primary;
					}
				}

			}

		}
	}
}
</style>